import React from 'react';
import { Bot, CheckCheck, User, Info, Clock } from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function isGroupedWithPrev(msg, prev) {
  if (!prev) return false;
  if (msg.senderType !== prev.senderType) return false;
  return Math.abs(new Date(msg.createdAt) - new Date(prev.createdAt)) < 2 * 60 * 1000;
}

export function isDifferentDay(msg, prev) {
  if (!prev) return false;
  return new Date(msg.createdAt).toDateString() !== new Date(prev.createdAt).toDateString();
}

// ─── Style map ───────────────────────────────────────────────────────────────
//
//  user     → incoming from WhatsApp client   → LEFT  gray  #F0F0F0
//  agent    → outgoing by AI                  → RIGHT green #DCF8C6  + Bot icon
//  operator → outgoing by human operator      → RIGHT blue  #DBEAFE  + User icon
//  system   → automated system notice         → CENTER gray italic
//
const STYLES = {
  user: {
    bg: '#F0F0F0',
    color: '#111111',
    side: 'left',
    Icon: null,
    badge: null,
  },
  agent: {
    bg: '#DCF8C6',
    color: '#111111',
    side: 'right',
    Icon: Bot,
    iconClass: 'text-emerald-600',
    badge: { label: 'IA', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  },
  operator: {
    bg: '#DBEAFE',
    color: '#111111',
    side: 'right',
    Icon: User,
    iconClass: 'text-blue-500',
    badge: { label: 'Vous', cls: 'bg-blue-100 text-blue-700 border border-blue-200' },
  },
};

// ─── Date separator ──────────────────────────────────────────────────────────

function DateSeparator({ date }) {
  const d = new Date(date);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  let label;
  if (d.toDateString() === now.toDateString()) label = "Aujourd'hui";
  else if (d.toDateString() === yesterday.toDateString()) label = 'Hier';
  else label = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="flex items-center gap-3 my-4 px-4">
      <div className="flex-1 h-px bg-primary-sidebar" />
      <span className="text-xs text-text-gray px-2 whitespace-nowrap">{label}</span>
      <div className="flex-1 h-px bg-primary-sidebar" />
    </div>
  );
}

// ─── System message ──────────────────────────────────────────────────────────

function SystemMessage({ message }) {
  return (
    <div className="flex justify-center items-center gap-1.5 my-3 px-4">
      <Info size={11} className="text-text-gray flex-shrink-0" />
      <span className="text-xs text-text-gray italic">{message.content}</span>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function MessageBubble({ message, prevMessage, contactName, isPending }) {
  const showDate = isDifferentDay(message, prevMessage);
  const grouped = isGroupedWithPrev(message, prevMessage);

  // ── System ──
  if (message.senderType === 'system') {
    return (
      <>
        {showDate && <DateSeparator date={message.createdAt} />}
        <SystemMessage message={message} />
      </>
    );
  }

  // ── User / Agent / Operator ──
  const style = STYLES[message.senderType] ?? STYLES.agent;
  const isLeft = style.side === 'left';

  const borderRadius = isLeft
    ? grouped ? '18px' : '18px 18px 18px 4px'
    : grouped ? '18px' : '18px 18px 4px 18px';

  const time = message.createdAt
    ? new Date(message.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : '';

  const showBadge = !isLeft && !grouped && style.badge;
  const showContactName = isLeft && !grouped && contactName;

  return (
    <>
      {showDate && <DateSeparator date={message.createdAt} />}

      <div className={`flex ${isLeft ? 'justify-start' : 'justify-end'} px-4 ${grouped ? 'mt-0.5' : 'mt-3'}`}>
        <div
          className={`flex flex-col ${isLeft ? 'items-start' : 'items-end'}`}
          style={{ maxWidth: '70%' }}
        >
          {/* Badge: IA or Vous — first bubble of a group only */}
          {showBadge && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full mb-1 ${style.badge.cls}`}>
              {style.badge.label}
            </span>
          )}

          {/* Contact name (incoming only) */}
          {showContactName && (
            <span className="text-xs text-text-gray ml-1 mb-0.5">{contactName}</span>
          )}

          {/* Bubble */}
          <div
            className="px-3 py-2 text-sm leading-relaxed break-words whitespace-pre-wrap shadow-sm"
            style={{ backgroundColor: style.bg, color: style.color, borderRadius, boxShadow: '0 1px 2px rgba(0,0,0,0.18)' }}
          >
            {message.content}
          </div>

          {/* Meta: icon + time + read / pending */}
          <div className={`flex items-center gap-1 mt-0.5 ${isLeft ? 'ml-1' : 'mr-1'}`}>
            {!isLeft && style.Icon && (
              <style.Icon size={10} className={style.iconClass} />
            )}
            <span className="text-[10px] text-text-gray">{time}</span>
            {/* Pending clock for optimistic messages not yet confirmed */}
            {isPending && <Clock size={10} className="text-blue-400 animate-pulse" />}
            {/* Double-check for confirmed sent messages */}
            {!isLeft && !isPending && message.isRead && (
              <CheckCheck size={11} className="text-blue-400" />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
